import React, { useRef, useState } from 'react';
import { FaTimes, FaFileWord } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  parseCatalogFromFile,
  importCatalogProducts,
} from '../utils/supplierCatalog';
import '../styles/InventoryProcurement.css';
import '../styles/InventoryProducts.css';

const CREATE = `
  mutation CreateSupplier($createSupplierInput: CreateSupplierInput!) {
    createSupplier(createSupplierInput: $createSupplierInput) { id }
  }
`;

const UPDATE = `
  mutation UpdateSupplier($updateSupplierInput: UpdateSupplierInput!) {
    updateSupplier(updateSupplierInput: $updateSupplierInput) { id }
  }
`;

export default function SupplierForm({ editData = null, onClose, onSaved }) {
  const submittingRef = useRef(false);
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [catalogRows, setCatalogRows] = useState([]);
  const [form, setForm] = useState({
    name: editData?.name || '',
    tradingName: editData?.tradingName || '',
    abn: editData?.abn || '',
    contactPerson: editData?.contactPerson || '',
    phone: editData?.phone || '',
    email: editData?.email || '',
    address: editData?.address || '',
    paymentTerms: editData?.paymentTerms || '',
    status: editData?.status || 'Active',
    notes: editData?.notes || '',
  });

  const isEdit = Boolean(editData?.id);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleCatalogFile = async (file) => {
    if (!file) return;
    setParsing(true);
    setFileName(file.name);
    try {
      const rows = await parseCatalogFromFile(file);
      if (!rows.length) {
        toast.error(
          'No products with prices found. Use a table or lines like “Bleach 5L $12.50”.',
        );
        setCatalogRows([]);
        return;
      }
      setCatalogRows(rows);
      toast.success(
        `Found ${rows.length} product${rows.length === 1 ? '' : 's'} in the file`,
      );
    } catch (err) {
      toast.error(err?.message || 'Could not read the Word file');
      setCatalogRows([]);
    } finally {
      setParsing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submittingRef.current || saving) return;
    if (!form.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!form.contactPerson.trim()) {
      toast.error('Contact person is required');
      return;
    }
    if (!form.phone.trim()) {
      toast.error('Phone is required');
      return;
    }
    if (!form.email.trim()) {
      toast.error('Email is required');
      return;
    }
    submittingRef.current = true;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        tradingName: form.tradingName.trim() || undefined,
        abn: form.abn.trim() || undefined,
        contactPerson: form.contactPerson.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim() || undefined,
        paymentTerms: form.paymentTerms.trim() || undefined,
        status: form.status,
        notes: form.notes.trim() || undefined,
      };
      let supplierId = editData?.id;
      if (isEdit) {
        await graphqlRequest(UPDATE, {
          updateSupplierInput: { id: editData.id, ...payload },
        });
      } else {
        const created = await graphqlRequest(CREATE, {
          createSupplierInput: payload,
        });
        supplierId = created?.createSupplier?.id;
        if (!supplierId) {
          throw new Error('Supplier was created but no id was returned');
        }
      }

      const selected = catalogRows.filter((r) => r.selected);
      if (supplierId && selected.length) {
        const res = await importCatalogProducts(supplierId, catalogRows);
        const createdN = Number(res?.created) || 0;
        const merged = Number(res?.merged) || 0;
        const failed = Number(res?.failed) || 0;
        if (createdN + merged > 0) {
          toast.success(
            `${isEdit ? 'Supplier updated' : 'Supplier added'} with ${
              createdN + merged
            } product${createdN + merged === 1 ? '' : 's'}${
              failed ? ` (${failed} failed)` : ''
            }`,
          );
        } else {
          toast.warn(
            isEdit
              ? 'Supplier updated, but no products were imported'
              : 'Supplier added, but no products were imported',
          );
        }
      } else {
        toast.success(isEdit ? 'Supplier updated' : 'Supplier added');
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to save supplier');
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  const selectedCount = catalogRows.filter((r) => r.selected).length;

  return (
    <div className="iproc-overlay" onClick={onClose}>
      <div
        className={`iproc-modal${!isEdit ? ' iproc-modal--wide' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="iproc-modal-header">
          <h2>{isEdit ? 'Edit Supplier' : 'Add Supplier'}</h2>
          <button type="button" className="iproc-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </header>
        <form className="iproc-modal-body" onSubmit={submit}>
          <div className="iproc-grid">
            <label className="iproc-field">
              <span>Company Name <em>*</em></span>
              <input value={form.name} onChange={(e) => set('name', e.target.value)} required />
            </label>
            <label className="iproc-field">
              <span>Trading Name</span>
              <input value={form.tradingName} onChange={(e) => set('tradingName', e.target.value)} />
            </label>
            <label className="iproc-field">
              <span>ABN / Registration</span>
              <input value={form.abn} onChange={(e) => set('abn', e.target.value)} />
            </label>
            <label className="iproc-field">
              <span>Contact Person <em>*</em></span>
              <input value={form.contactPerson} onChange={(e) => set('contactPerson', e.target.value)} required />
            </label>
            <label className="iproc-field">
              <span>Phone <em>*</em></span>
              <input value={form.phone} onChange={(e) => set('phone', e.target.value)} required />
            </label>
            <label className="iproc-field">
              <span>Email <em>*</em></span>
              <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required />
            </label>
            <label className="iproc-field iproc-field--full">
              <span>Address</span>
              <input value={form.address} onChange={(e) => set('address', e.target.value)} />
            </label>
            <label className="iproc-field iproc-field--full">
              <span>Payment Terms</span>
              <input
                value={form.paymentTerms}
                onChange={(e) => set('paymentTerms', e.target.value)}
                placeholder="e.g. Net 30"
              />
            </label>
            <label className="iproc-field">
              <span>Status</span>
              <select
                value={form.status === 'On Hold' ? 'OnHold' : form.status}
                onChange={(e) => set('status', e.target.value)}
              >
                <option value="Active">Active</option>
                <option value="OnHold">On Hold</option>
                <option value="Inactive">Inactive</option>
              </select>
            </label>
            <label className="iproc-field iproc-field--full">
              <span>Notes</span>
              <textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </label>
          </div>

          {!isEdit ? (
            <section className="iproc-catalog-block">
              <h3>
                <FaFileWord /> Product catalog (optional)
              </h3>
              <p className="ip-bulk-hint">
                Drop this supplier’s Word price list (.docx). Products and
                prices are read from the file so you do not type each item.
              </p>
              <div
                className={`iproc-drop${dragOver ? ' is-over' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  void handleCatalogFile(e.dataTransfer.files?.[0]);
                }}
                onClick={() => fileRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    fileRef.current?.click();
                  }
                }}
              >
                <FaFileWord />
                <strong>
                  {parsing ? 'Reading Word file…' : 'Drop Word file here'}
                </strong>
                <span>or click to choose .docx / CSV</span>
                {fileName ? <em>{fileName}</em> : null}
                <input
                  ref={fileRef}
                  type="file"
                  accept=".docx,.doc,.csv,.txt,.tsv"
                  hidden
                  onChange={(e) => void handleCatalogFile(e.target.files?.[0])}
                />
              </div>

              {catalogRows.length > 0 ? (
                <>
                  <div className="ip-bulk-table-wrap" style={{ maxHeight: 220, marginTop: 12 }}>
                    <table className="ip-bulk-table">
                      <thead>
                        <tr>
                          <th />
                          <th>Product</th>
                          <th>Price</th>
                          <th>Unit</th>
                          <th>Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catalogRows.map((row) => (
                          <tr key={row.key}>
                            <td>
                              <input
                                type="checkbox"
                                checked={row.selected}
                                onChange={(e) =>
                                  setCatalogRows((prev) =>
                                    prev.map((r) =>
                                      r.key === row.key
                                        ? { ...r, selected: e.target.checked }
                                        : r,
                                    ),
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                value={row.name}
                                onChange={(e) =>
                                  setCatalogRows((prev) =>
                                    prev.map((r) =>
                                      r.key === row.key
                                        ? { ...r, name: e.target.value }
                                        : r,
                                    ),
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={row.perItemCost}
                                onChange={(e) =>
                                  setCatalogRows((prev) =>
                                    prev.map((r) =>
                                      r.key === row.key
                                        ? { ...r, perItemCost: e.target.value }
                                        : r,
                                    ),
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                value={row.unit}
                                onChange={(e) =>
                                  setCatalogRows((prev) =>
                                    prev.map((r) =>
                                      r.key === row.key
                                        ? { ...r, unit: e.target.value }
                                        : r,
                                    ),
                                  )
                                }
                              />
                            </td>
                            <td>
                              <input
                                value={row.category}
                                onChange={(e) =>
                                  setCatalogRows((prev) =>
                                    prev.map((r) =>
                                      r.key === row.key
                                        ? { ...r, category: e.target.value }
                                        : r,
                                    ),
                                  )
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="ip-bulk-count" style={{ marginTop: 8 }}>
                    {selectedCount} product{selectedCount === 1 ? '' : 's'} will
                    be added with this supplier
                  </p>
                </>
              ) : null}
            </section>
          ) : null}

          <div className="iproc-modal-footer">
            <button type="button" className="iproc-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="iproc-btn iproc-btn--primary" disabled={saving || parsing}>
              {saving
                ? 'Saving…'
                : isEdit
                  ? 'Save changes'
                  : selectedCount
                    ? `Add Supplier + ${selectedCount} products`
                    : 'Add Supplier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
